/**
 * The format controls, DESCRIBED rather than drawn.
 *
 * The popup is a walk over this list and the row's badges are a second walk
 * over the same list, so a new display attribute is one entry here plus naming
 * it in `FORMAT_CONTROLS` — there is no layout to touch, no second place that
 * has to learn the option exists, and no way for the panel and the badges to
 * disagree about what is set.
 *
 * Excel's Format Cells dialog needs a Category list because a spreadsheet cell
 * has no type. Ours has one: the model declares what a measure is, so the unit
 * names its controls (`FORMAT_CONTROLS` in `@clockwise/semantic-core`) and the
 * reader never picks a type they already fixed by picking the measure.
 * Accounting is the one variant that survives that reduction, and it stays a
 * checkbox rather than a type, because it IS currency — with the symbol and
 * the decimal point aligned down the column.
 */
import type {
  Measure,
  ResolvedFormat,
  ScaleStyle,
  UIReportSpec,
  ValueFormat,
  ValueFormatKey,
} from '@clockwise/semantic-core';
import {
  type FormatRule,
  type NegativeStyle,
  type RuleOp,
  resolveValueFormat,
  SCALES,
} from '@clockwise/semantic-core';
import { formatResolved } from '../format';

/** What a control is drawn and labelled against. */
export interface FormatContext {
  measure: Measure;
  /** What this measure reads as RIGHT NOW, every fallback applied. */
  resolved: ResolvedFormat;
  /**
   * What it would read as if the reader had chosen NOTHING.
   *
   * Every control needs it, and none of them can derive it: "is this the
   * default?" and "what does Default say?" are the same question, and the
   * answer moves with the measure and with `showAs`.
   */
  inherited: ResolvedFormat;
}

/**
 * How a control draws. One kind per shape.
 *
 * `rules` is the one that breaks this module's "a new attribute is one entry
 * here and no layout to touch" promise, and it is worth saying why rather than
 * quietly widening the union. Every other control edits a SCALAR — a count, a
 * flag, one of a fixed list — so the popup can draw it from `label` + `kind`
 * alone. A highlight rule is a record, and an author holds several in an order
 * that decides which of them wins, so the control has to add, remove, reorder
 * and edit three fields per row. That is a shape, not a value, and it gets its
 * own branch in `format-popup`.
 */
export type ControlKind = 'list' | 'choices' | 'toggle' | 'rules';

export interface FormatOption<K extends ValueFormatKey = ValueFormatKey> {
  id: K;
  /** The control's own name, and the name its badge is cleared by. */
  label: string;
  kind: ControlKind;
  /**
   * The badge text when this option is set — often the option RENDERED, so the
   * row says what the column will look like without anything being opened.
   */
  badge: (value: NonNullable<ValueFormat[K]>, ctx: FormatContext) => string;
  /**
   * Does this value take the negative colour in its badge?
   *
   * NOT a highlight tone. `RuleTone` in `@clockwise/semantic-core` is the
   * four-colour palette a threshold paints a CELL with; this is one boolean
   * about how the badge for this option renders in the panel.
   */
  tone?: (value: NonNullable<ValueFormat[K]>) => boolean;
  /**
   * Is this value the one the measure would have had anyway?
   *
   * Absence means inherit, so a control that lands back on the inherited
   * answer must DELETE its key rather than store it — otherwise the report is
   * pinned to today's default and stops tracking the model.
   */
  isDefault: (value: NonNullable<ValueFormat[K]>, ctx: FormatContext) => boolean;
}

/**
 * How each comparison reads on the BADGE, where width is scarce.
 *
 * Symbols here and words in the control, because the two are read differently:
 * a badge is scanned beside four others on a crowded row, and `< $16,000.00`
 * survives that where a sentence does not.
 */
export const RULE_OP_LABELS: Record<RuleOp, string> = {
  lt: '<',
  lte: '≤',
  gt: '>',
  gte: '≥',
};

/**
 * How each comparison reads in the CONTROL, and the reason the pairs exist.
 *
 * NOT `<` / `≤`, and not Excel's "less than or equal to" either. The boundary
 * is the whole difference between a strict operator and its inclusive twin, and
 * it is the thing an author gets wrong first — `> 1000` does not include 1000,
 * `≥ 100%` does. "at or below" says that in the option itself, where a glyph
 * makes the reader remember which way the line under it leans and Excel's
 * phrasing buries the boundary at the end of five words.
 *
 * It matters more here than in a spreadsheet because three of the budget
 * measures are floored at zero: on `Remaining Hours`, `below 0` can never fire
 * and `at or below 0` is the rule somebody actually wants.
 */
export const RULE_OP_WORDS: Record<RuleOp, string> = {
  lt: 'below',
  lte: 'at or below',
  gt: 'above',
  gte: 'at or above',
};

/**
 * One rule as a sentence, for the hint under the control.
 *
 * Names the TONE as well as the comparison, so a reader checking three rules
 * reads the same order the resolver applies them in — which is what makes
 * "the first match wins" mean something concrete rather than a rule of thumb.
 */
export function ruleSentence(rule: FormatRule, resolved: ResolvedFormat): string {
  const shown = resolved.format === 'percent' ? rule.value / 100 : rule.value;
  const text = formatResolved(shown, thresholdFormat(resolved));
  return `${rule.tone} ${RULE_OP_WORDS[rule.op]} ${text}`;
}

/**
 * One rule, written the way the column reads it.
 *
 * 🔴 The stored threshold is in DISPLAY units and `formatResolved` takes the
 * cell's own, so a percent rule divides back out before rendering — the exact
 * inverse of what `toneFor` does on the way in. Get this backwards and a rule
 * an author typed as `100` reads back as `10,000%`.
 *
 * The sign style is forced to `minus` and zero to `number` for the same reason
 * `negativeSample` does it: this is a threshold being quoted, not a cell being
 * drawn, and a threshold of 0 shown as a dash names nothing.
 */
export function ruleSample(rule: FormatRule, resolved: ResolvedFormat): string {
  const shown = resolved.format === 'percent' ? rule.value / 100 : rule.value;
  const text = formatResolved(shown, thresholdFormat(resolved));
  return `${RULE_OP_LABELS[rule.op]} ${text}`;
}

/**
 * How a threshold is QUOTED: the column's own format, minus the parts that
 * would misquote it.
 *
 * The scale goes too. A threshold is typed in whole units, and quoted at the
 * column's scale it rounds — `below 400` in thousands would read `< 0K`, a
 * rule that looks like it can never fire.
 */
function thresholdFormat(resolved: ResolvedFormat): ResolvedFormat {
  return { ...resolved, negative: 'minus', zero: 'number', scale: 'none' };
}

/** The literal rendering of one negative style, at this measure's format. */
export function negativeSample(style: NegativeStyle, resolved: ResolvedFormat): string {
  // Excel's own illustrative value, so the list reads the way the dialog a
  // finance user already knows does — lifted to the column's scale, so a
  // column in millions shows `($1,234.1M)` rather than `($0.0M)`.
  // A percent cell holds a RATIO: -1234.1 would print as -123,410%.
  const value = resolved.format === 'percent' ? -0.1234 : -1234.1;
  const lift = resolved.scale === 'none' ? 1 : SCALES[resolved.scale].divisor;
  return formatResolved(value * lift, { ...resolved, negative: style, zero: 'number' });
}

/**
 * Rounding is TWO questions, asked as two rows: how many decimal places, and
 * whether to show the column in thousands or millions.
 *
 * It was one list of seven combinations drawn at an invented 1,234,567.89,
 * which made the reader diff seven numbers to find which of the two things
 * changed — and on an hours column running 0–40, `1.2M` said nothing about
 * their data. Each row now answers one question, and the decimal row is drawn
 * with a value from THE COLUMN ITSELF.
 *
 * No "trim trailing zeros" (`0.##`): Excel and Sheets print a whole number
 * under that pattern with a dangling point — `12.` — so the file would disagree
 * with the screen.
 */
export const DECIMAL_CHOICES = [0, 1, 2] as const;

/**
 * The decimal places offered, ascending: 0, 1, 2 — plus the inherited and the
 * current answer when either is outside that, because a model can declare four
 * places and a row that could not show the column's own state selects nothing.
 */
export function decimalChoices(ctx: FormatContext): number[] {
  const all = new Set<number>([...DECIMAL_CHOICES, ctx.inherited.decimals, ctx.resolved.decimals]);
  return [...all].sort((a, b) => a - b);
}

/**
 * The value the decimal row is drawn with: the column's LARGEST magnitude, so
 * the reader sees their own number at each precision — `22` · `22.2` · `22.23`.
 *
 * Magnitude, not the maximum: a column of losses would otherwise sample its
 * smallest loss. Falls back to an illustrative value when there is nothing to
 * read — no result yet, an empty column — and under a `showAs` share, where the
 * cells are percentages and the raw values the result carries are not what the
 * reader is looking at.
 */
export function columnSample(
  values: readonly (number | null | undefined)[] | undefined,
  resolved: ResolvedFormat,
): number {
  const fallback = resolved.format === 'percent' ? 0.61234 : 1234.567;
  if (!values || resolved.mode !== 'raw') return fallback;
  let best: number | undefined;
  for (const v of values) {
    if (v === null || v === undefined || !Number.isFinite(v)) continue;
    if (best === undefined || Math.abs(v) > best) best = Math.abs(v);
  }
  // An all-zero column samples as the fallback: `0` · `0.0` · `0.00` shows the
  // places, but reads as though the column has no data.
  return best === undefined || best === 0 ? fallback : best;
}

/**
 * One entry in either row, rendered: the sample at these places, at this
 * scale. Sign and zero styles are the column's business, not this row's — it
 * is answering "how many digits", so the sample is always a plain positive.
 */
export function roundingSample(
  sample: number,
  resolved: ResolvedFormat,
  over: { decimals?: number; scale?: ScaleStyle | 'none' },
): string {
  return formatResolved(sample, { ...resolved, ...over, negative: 'minus', zero: 'number' });
}

/** How each scale is named on its button. */
export const SCALE_LABELS: Record<ScaleStyle | 'none', string> = {
  none: 'None',
  thousands: 'K',
  millions: 'M',
};

type OptionMap = { [K in ValueFormatKey]: FormatOption<K> };

/**
 * Every control, keyed by the `ValueFormat` key it sets. Draw order is the
 * order a reader asks the questions: what the number IS, then how it is
 * signed, then the edge cases.
 */
export const FORMAT_OPTIONS: OptionMap = {
  decimals: {
    id: 'decimals',
    label: 'Decimal places',
    // Drawn by the Rounding list, together with `scale`.
    kind: 'list',
    badge: (value) => `${value} dp`,
    isDefault: (value, ctx) => value === ctx.inherited.decimals,
  },
  scale: {
    id: 'scale',
    label: 'Scale',
    kind: 'list',
    badge: (value) => `in ${SCALES[value].suffix}`,
    // Absence is the whole-value default, so any stored scale is a setting.
    isDefault: () => false,
  },
  symbol: {
    id: 'symbol',
    label: 'Symbol',
    kind: 'choices',
    badge: (value) => (value === 'code' ? 'USD' : 'no symbol'),
    isDefault: (value) => value === 'symbol',
  },
  accounting: {
    id: 'accounting',
    label: 'Accounting',
    kind: 'toggle',
    badge: () => 'Accounting',
    isDefault: (value) => value !== true,
  },
  negative: {
    id: 'negative',
    label: 'Negative numbers',
    kind: 'list',
    badge: (value, ctx) => negativeSample(value, ctx.resolved),
    tone: (value) => value === 'redOnly' || value === 'minusRed' || value === 'parensRed',
    isDefault: (value) => value === 'minus',
  },
  zero: {
    id: 'zero',
    label: 'Show zero as',
    kind: 'choices',
    badge: (value) => (value === 'dash' ? 'zero -' : 'zero blank'),
    isDefault: (value) => value === 'number',
  },
  separators: {
    id: 'separators',
    label: 'Separators',
    kind: 'toggle',
    badge: () => 'no separators',
    isDefault: (value) => value !== false,
  },
  rules: {
    id: 'rules',
    label: 'Highlight',
    kind: 'rules',
    // One rule is quoted in full, because that is the common case and "1 rule"
    // makes the reader open the popup to learn something the badge had room
    // for. Past one there is no honest summary — the tones differ and the
    // order decides which wins — so it counts.
    badge: (value, ctx) =>
      value.length === 1 && value[0] !== undefined
        ? ruleSample(value[0], ctx.resolved)
        : `${value.length} rules`,
    // AN EMPTY ARRAY IS NOT A SETTING. The popup deletes the key when the last
    // rule goes, but a spec can arrive hand-edited or half-migrated, and a
    // badge reading "0 rules" on a column with no highlight is noise.
    isDefault: (value) => value.length === 0,
  },
};

/**
 * Narrowed access to one option.
 *
 * Indexing the map with a UNION key yields a union of functions, which cannot
 * be called with a union argument. These three keep the cast in one place
 * rather than at every call site.
 */
function optionFor<K extends ValueFormatKey>(key: K): FormatOption<K> {
  return FORMAT_OPTIONS[key];
}

export function badgeFor(key: ValueFormatKey, chosen: ValueFormat, ctx: FormatContext): string {
  const value = chosen[key];
  if (value === undefined) return '';
  return optionFor(key).badge(value as never, ctx);
}

export function badgeIsNegative(key: ValueFormatKey, chosen: ValueFormat): boolean {
  const value = chosen[key];
  if (value === undefined) return false;
  return optionFor(key).tone?.(value as never) ?? false;
}

/**
 * The keys actually doing something on this measure, in draw order.
 *
 * Filtered by what APPLIES — a currency symbol on an hours measure is not a
 * setting, whatever the spec happens to carry — and then by whether the stored
 * value differs from the inherited one, so a key that matches the default
 * shows no badge even if something wrote it.
 */
export function activeFormatKeys(chosen: ValueFormat, ctx: FormatContext): ValueFormatKey[] {
  return ctx.resolved.controls.filter((key) => {
    const value = chosen[key];
    if (value === undefined) return false;
    return !optionFor(key).isDefault(value as never, ctx);
  });
}

/** One measure's controls, resolved and ready to draw. */
export function formatContext(
  measure: Measure,
  spec: Pick<UIReportSpec, 'showAs' | 'valueFormat'>,
): FormatContext {
  return {
    measure,
    resolved: resolveValueFormat(measure, spec, measure.id),
    // The same spec MINUS this measure's choices, so `showAs` still applies —
    // "Default" under a share is the percent unit's precision, not the
    // measure's own.
    inherited: resolveValueFormat(measure, { showAs: spec.showAs, valueFormat: {} }, measure.id),
  };
}
